import React, { Suspense } from "react";
import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthProvider, useAuth } from "./providers/AuthProvider";

import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import OTPVerification from "./pages/OTPVerification";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Invoices from "./pages/Invoices";
import AddInvoice from "./pages/AddInvoice";
import Inventory from "./pages/Inventory";
import InventoryRequest from "./pages/InventoryRequest";
import InventoryLogs from "./pages/InventoryLogs";
import Payments from "./pages/Payments";
import Accounts from "./pages/Accounts";
import DisabledAccounts from "./pages/DisabledAccounts";
import Settlements from "./pages/Settlements";
import Payouts from "./pages/Payouts";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Reconciliation from "./pages/Reconciliation";
import TagSettings from "./pages/TagSettings";
import RoleSettings from "./pages/RoleSettings";
import { ProtectedRoute, PublicRoute, SettingsLayout } from "./layouts";
import TeamManagement from "./pages/TeamManagement";
import WarehouseSettings from "./pages/WarehouseSettings";
import AccountTier from "./pages/AccountTier";
import ResetPassword from "./pages/ResetPassword";
import InvoiceGroups from "./pages/InvoiceGroups";
import Loader from "./components/Loader";
import SubAccounts from "./pages/SubAccounts";
import Wallet from "./pages/Wallet";
import Error from "./pages/Error";
import {
	ArchivedCustomersRedirect,
	ArchivedInventoryRedirect,
	ArchivedInvoicesRedirect,
	CustomerDetailRedirect,
	InvoiceDetailRedirect,
	SettlementDetailRedirect,
} from "./pages/redirects";

const Landing = () => {
	const { isAuthenticated } = useAuth();

	return isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />;
};

const App = () => {
	const router = createBrowserRouter([
		{
			element: <AuthProvider />,
			ErrorBoundary: Error,
			children: [
				{
					path: "/",
					element: <Landing />,
				},
				{
					element: <PublicRoute />,
					children: [
						{ path: "/signup", element: <Signup /> },
						{ path: "/login", element: <Login /> },
						{ path: "/otp-verification", element: <OTPVerification /> },
						{ path: "/forgot-password", element: <ForgotPassword /> },
						{ path: "/reset-password", element: <ResetPassword /> },
					],
				},
				{
					element: <ProtectedRoute />,
					children: [
						{ path: "/dashboard", element: <Dashboard /> },
						{ path: "/customers", element: <Customers /> },
						{ path: "/sub-accounts", element: <SubAccounts /> },
						{ path: "/customers/archived", element: <ArchivedCustomersRedirect /> },
						{ path: "/customers/:uuid", element: <CustomerDetailRedirect /> },
						{ path: "/invoices", element: <Invoices /> },
						{ path: "/invoice-groups", element: <InvoiceGroups /> },
						{ path: "/invoices/archived", element: <ArchivedInvoicesRedirect /> },
						{ path: "/invoices/add", element: <AddInvoice /> },
						{ path: "/invoices/view/:uuid", element: <InvoiceDetailRedirect /> },
						{ path: "/inventory", element: <Inventory /> },
						{ path: "/inventory/archived", element: <ArchivedInventoryRedirect /> },
						{ path: "/inventory/requests", element: <InventoryRequest /> },
						{ path: "/inventory/logs", element: <InventoryLogs /> },
						{ path: "/payments", element: <Payments /> },
						{ path: "/wallet", element: <Wallet /> },
						{ path: "/accounts", element: <Accounts /> },
						{ path: "/disabled-accounts", element: <DisabledAccounts /> },
						{ path: "/settlements", element: <Settlements /> },
						{ path: "/settlements/:id", element: <SettlementDetailRedirect /> },
						{ path: "/reconciliation", element: <Reconciliation /> },
						{ path: "/payouts", element: <Payouts /> },
						{
							// Settings pages share the settings shell (left nav + identity header),
							// matching the prototype's single Settings screen with sub-panels.
							element: <SettingsLayout />,
							children: [
								{ path: "/team", element: <TeamManagement /> },
								{ path: "/notifications", element: <Notifications /> },
								{ path: "/settings", element: <Settings /> },
								{ path: "/profile", element: <Profile /> },
								{ path: "/tags", element: <TagSettings /> },
								{ path: "/roles", element: <RoleSettings /> },
								{ path: "/warehouse", element: <WarehouseSettings /> },
								{ path: "/account", element: <AccountTier /> },
							],
						},
					],
				},
			],
		},
	]);

	return (
		<Suspense fallback={<Loader />}>
			<RouterProvider router={router} />
		</Suspense>
	);
};

export default App;
